// apps/web/src/hooks/useOutsideClick.ts
//
// Calls `onOutside` on any mousedown that lands outside the given ref(s).
// Accepts one ref or an array (e.g. a trigger button plus a portaled popover
// body, the InfoTooltip pattern). Pass stable refs from useRef; the callback
// itself may be inline (latest-value ref pattern).

import { useEffect, useRef, type RefObject } from 'react';

type AnyElementRef = RefObject<HTMLElement | null>;

export function useOutsideClick(
	refs: AnyElementRef | AnyElementRef[],
	onOutside: () => void,
	active: boolean = true,
): void {
	const cb = useRef<() => void>(onOutside);
	cb.current = onOutside;

	useEffect(() => {
		if (!active) return;
		const list: AnyElementRef[] = Array.isArray(refs) ? refs : [refs];
		const onDown = (e: MouseEvent): void => {
			const target = e.target as Node;
			if (list.some((r) => r.current?.contains(target))) return;
			cb.current();
		};
		document.addEventListener('mousedown', onDown);
		return () => document.removeEventListener('mousedown', onDown);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active, ...(Array.isArray(refs) ? refs : [refs])]);
}
